// @ts-nocheck
import { destroyAccreditationBody } from '../_data_sources/AccreditationBodies/manageAccreditationBodies';
import { getAccreditationBodies } from '../_data_sources/AccreditationBodies/useAccreditationBodies';
import { getAccreditationVersions } from '../_data_sources/AccreditationVersions/useAccreditationVersions';
import { destroyAccreditationVersion } from '../_data_sources/AccreditationVersions/manageAccreditationVersions';
import { fetchAccreditationStandards } from '../_data_sources/AccreditationStandards/useStandards';
import { destroyStandard } from '../_data_sources/AccreditationStandards/manageStandard';

declare global {
  interface Window {
    cleanBodies: (bodyIds?: string[]) => Promise<void>;
  }
}

// UUID regex pattern
const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

// Recursively delete standards and their children
async function deleteStandardAndChildren(
  bodyId: string,
  versionId: string,
  standard: any,
  level: number = 0
): Promise<boolean> {
  const indent = '  '.repeat(level);
  console.log(`${indent}Processing standard ${standard.id} (level ${level})`);
  
  let hasIssues = false;
  
  // If this standard has children, process them first
  if (standard.standardCount > 0) {
    console.log(`${indent}Standard ${standard.id} has ${standard.standardCount} children`);
    const childStandards = await fetchAccreditationStandards(bodyId, versionId, standard.id);
    
    // Process each child standard
    for (const childStandard of childStandards) {
      const childSuccess = await deleteStandardAndChildren(bodyId, versionId, childStandard, level + 1);
      if (!childSuccess) {
        hasIssues = true;
      }
    }
  }
  
  // Only delete this standard if all its children were deleted successfully
  if (!hasIssues) {
    try {
      console.log(`${indent}Deleting standard ${standard.id}`);
      await destroyStandard(bodyId, versionId, standard.id);
      return true;
    } catch (error) {
      console.error(`${indent}Failed to delete standard ${standard.id}:`, error);
      return false;
    }
  } else {
    console.log(`${indent}Skipping deletion of standard ${standard.id} due to child deletion failures`);
    return false;
  }
}

window.cleanBodies = async (bodyIds?: string[]) => {
  console.log('Starting cleanBodies...');
  if (bodyIds) {
    console.log(`Will only process bodies with IDs: ${bodyIds.join(', ')}`);
  }
  
  try {
    // Get all accreditation bodies with page size of 100
    const response = await getAccreditationBodies({ size: 100 });
    const bodies = response.content;

    console.log(`Found ${bodies.length} accreditation bodies to process`);

    // Process each body
    for (const body of bodies) {
      // Skip if bodyIds is provided and this body's ID is not in the list
      if (bodyIds && !bodyIds.includes(body.id)) {
        console.log(`Skipping body ${body.id} as it's not in the target list`);
        continue;
      }

      if (UUID_PATTERN.test(body.name)) {
        console.log(`Found body with UUID in name: ${body.name}`);
        
        // Get and delete all versions for this body
        const versionsResponse = await getAccreditationVersions(body.id, { size: 100 });
        const versions = versionsResponse.content;
        
        console.log(`Found ${versions.length} versions to delete for body ${body.id}`);
        
        // Track if any version had issues
        let bodyHasIssues = false;
        
        // Process each version
        for (const version of versions) {
          console.log(`Processing version ${version.id} for body ${body.id}`);
          
          // Get top-level standards for this version
          const standards = await fetchAccreditationStandards(body.id, version.id);
          console.log(`Found ${standards.length} top-level standards to delete for version ${version.id}`);
          
          // Track if any standard had issues
          let versionHasIssues = false;
          
          // Process each top-level standard and its children
          for (const standard of standards) {
            const standardSuccess = await deleteStandardAndChildren(body.id, version.id, standard);
            if (!standardSuccess) {
              versionHasIssues = true;
              bodyHasIssues = true;
            }
          }
          
          // Only delete version if all its standards were deleted successfully
          if (!versionHasIssues) {
            try {
              console.log(`Deleting version ${version.id} for body ${body.id}`);
              await destroyAccreditationVersion(body.id, version.id);
            } catch (error) {
              console.error(`Failed to delete version ${version.id}:`, error);
              bodyHasIssues = true;
            }
          } else {
            console.log(`Skipping deletion of version ${version.id} due to standard deletion failures`);
          }
        }
        
        // Only delete body if all its versions were deleted successfully
        if (!bodyHasIssues) {
          try {
            console.log(`Deleting accreditation body with ID: ${body.id}`);
            await destroyAccreditationBody(body.id);
          } catch (error) {
            console.error(`Failed to delete body ${body.id}:`, error);
          }
        } else {
          console.log(`Skipping deletion of body ${body.id} due to version/standard deletion failures`);
        }
      }
    }

    console.log('cleanBodies completed successfully!');
  } catch (error) {
    console.error('Error in cleanBodies:', error);
  }
}; 